import React, { useMemo, useRef, useState } from 'react'

import { Link } from 'react-router-dom'

import { DrawerRef } from 'javascript/common/components/Drawer'

import { DashboardOutlined, ProfileOutlined, ScheduleOutlined } from '@ant-design/icons'

import { observer } from 'mobx-react-lite'

import { ContractorIcon } from 'common/components/ContractorIcon'
import { NavBar } from 'common/components/MainMenu/NavBar'
import { HelpModal, HelpModalRef } from 'common/components/MainMenu/UserMenu'
import { Notifications } from 'common/components/Notifications'
import { formatName } from 'common/helpers/formatters'

import { UpgradeDrawer } from 'vendor/components/Billing/upgrade_drawer'
import { useVendorStores } from 'vendor/hooks/use-stores'
import { sign_out } from 'vendor/server/user'

import Masquerade from './masquerade'

const signOutAndReload = async () => {
  await sign_out()
  // Prevent redirect to /, keeping in vendor login
  window.location.assign('/vendor')
}

const insertIf = (condition, ...elements) => (condition ? elements : [])

export const MainMenu = observer(() => {
  const { userStore, notificationStore } = useVendorStores()
  const helRef = useRef<HelpModalRef>()
  const drawerUpdateRef = React.useRef<DrawerRef>()
  const handleUpgradeButtonClick = () => drawerUpdateRef.current?.show()

  const [masqueradeModalOpen, setMasqueradeModal] = useState(false)

  const userName = formatName(userStore.currentUser?.first_name, userStore.currentUser?.last_name)

  const navItems = [
    {
      key: 'order',
      match: /^\/(vendor\/order\/|vendor\/orders$)/,
      href: '/vendor/orders',
      label: 'Orders',
      icon: <ScheduleOutlined />,
    },
    ...insertIf(userStore.canUseOrderMaterials, {
      key: 'order_materials',
      match: /^\/(vendor\/order_materials)$/,
      href: '/vendor/order_materials',
      label: 'Materials',
      icon: <ProfileOutlined />,
    }),
    ...insertIf(userStore.canUseWatchersByCustomer, {
      key: 'customer',
      match: /^\/(vendor\/customers)/,
      href: '/vendor/customers',
      label: 'Customers',
      icon: <ContractorIcon />,
    }),
    ...insertIf(userStore.canUseDashboard, {
      key: 'dashboard',
      match: /^\/(vendor\/dashboard)/,
      href: '/vendor/dashboard',
      label: 'Dashboard',
      icon: <DashboardOutlined />,
    }),
  ]

  const adminExtraItems = userStore.currentUser?.is_subbase
    ? [
        { label: <a href="/admin">Admin</a>, key: 'admin' },
        {
          label: (
            <>
              <div onClick={() => setMasqueradeModal(true)}>Login As User</div>
              <Masquerade setMasqueradeModal={setMasqueradeModal} masqueradeModalOpen={masqueradeModalOpen} />
            </>
          ),
          key: 'masquerade',
        },
      ]
    : []

  const extraItems = useMemo(() => {
    if (!userStore.currentUser) {
      return []
    }

    const hasCustomerId = !!userStore.currentVendor.billing?.stripe_customer_id

    return [
      userStore.canManageSettings && {
        label: <Link to="/vendor/settings">Vendor Settings</Link>,
        key: 'vendor_settings',
      },
      hasCustomerId && { label: <a href="/vendor/billing/billing_session">Billing</a>, key: 'billing' },
    ].filter(Boolean)
  }, [userStore.currentUser, helRef?.current, userStore.canManageSettings])

  const userExtraItems = useMemo(() => {
    if (!userStore.currentUser) {
      return []
    }

    return [
      { label: <Link to="/vendor/user_settings">User Settings</Link>, key: 'user_settings' },
      ...insertIf(!userStore.canUseDashboard, {
        label: <div onClick={handleUpgradeButtonClick}>Upgrade</div>,
        key: 'upgrade',
      }),
      {
        label: (
          <>
            <div onClick={() => helRef?.current?.show()}>Help</div>
            <HelpModal ref={helRef} />
          </>
        ),
        key: 'help',
      },
      { label: <div onClick={signOutAndReload}>Sign Out</div>, key: 'sign_out' },
    ]
  }, [userStore.currentUser, helRef?.current])

  return (
    <>
      <NavBar
        basePath="/vendor/orders"
        navItems={navItems}
        extraItems={extraItems}
        userExtraItems={userExtraItems}
        adminExtraItems={adminExtraItems}
        notification={
          <Notifications
            onLoadMore={notificationStore.loadMoreNotifications}
            notificationsGroups={notificationStore.notificationGroups.slice()}
            onDismissNotificationGroup={notificationStore.dismissGroup}
            onDismissAllNotifications={notificationStore.dismissAll}
            loadMessages={notificationStore.loadMessages}
            dismissMessage={notificationStore.dismiss}
            unreadNotificationsCount={notificationStore.unreadNotificationsCount}
            totalUnreadCount={notificationStore.totalUnreadNotificationsCount}
          />
        }
        userName={userName}
        breakpoint="md"
      />
      <UpgradeDrawer ref={drawerUpdateRef} />
    </>
  )
})
